import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

import { useAuthContext } from '~/shared/context/useAuthContext';
import { useExportFetch } from '~/shared/hooks/useExportFetch';
import { usePermission } from '~/shared/hooks/usePermission';
import { useXFetch } from '~/shared/utils/useXFetch';

import { toaster } from '~/shared/utils/toaster';

import { executeReportsExport, type ReportsExportParams } from './export-state/reportsExport';
import { executeVolumesExport, type VolumesExportParams } from './export-state/volumesExport';

const ExportStateContext = createContext<ExportStateContextType | undefined>(undefined);

type ExportKey = 'volumes' | 'reports';

export interface ExportStateContextType {
  isExporting: (key: ExportKey) => boolean;
  triggerVolumesExport: (params: VolumesExportParams) => void;
  triggerReportsExport: (params: ReportsExportParams) => void;
}

interface RunGuardedExportOptions {
  key: ExportKey;
  runningRef: React.MutableRefObject<boolean>;
  addKey: (key: ExportKey) => void;
  removeKey: (key: ExportKey) => void;
  runTask: () => Promise<void>;
}

const EXPORT_STARTED_TOAST = {
  variant: 'success' as const,
  title: 'Export started',
  message: 'This may take a few moments',
};

const EXPORT_FAILED_TOAST = {
  variant: 'error' as const,
  title: 'Export failed',
  message: 'Please try again.',
};

function runGuardedExport({
  key,
  runningRef,
  addKey,
  removeKey,
  runTask,
}: RunGuardedExportOptions): void {
  if (runningRef.current) return;
  runningRef.current = true;

  addKey(key);
  toaster(EXPORT_STARTED_TOAST);

  void (async () => {
    try {
      await runTask();
    } catch {
      toaster(EXPORT_FAILED_TOAST);
    } finally {
      runningRef.current = false;
      removeKey(key);
    }
  })();
}

export function ExportStateProvider({ children }: { children: ReactNode }) {
  const [exportingKeys, setExportingKeys] = useState<Set<ExportKey>>(new Set());
  const { activeTenantName } = useAuthContext();
  const canReadUsers = usePermission('datafy.read.users');
  const { fetchDataInBatches } = useExportFetch();
  const xfetch = useXFetch();
  const isVolumesRunningRef = useRef(false);
  const isReportsRunningRef = useRef(false);

  const isExporting = useCallback(
    (key: ExportKey) => exportingKeys.has(key),
    [exportingKeys]
  );

  const addKey = useCallback((key: ExportKey) => {
    setExportingKeys((prev) => new Set([...prev, key]));
  }, []);

  const removeKey = useCallback((key: ExportKey) => {
    setExportingKeys((prev) => {
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const triggerVolumesExport = useCallback(
    (params: VolumesExportParams) => {
      runGuardedExport({
        key: 'volumes',
        runningRef: isVolumesRunningRef,
        addKey,
        removeKey,
        runTask: () =>
          executeVolumesExport(params, {
            fetchDataInBatches,
            activeTenantName,
          }),
      });
    },
    [addKey, removeKey, fetchDataInBatches, activeTenantName]
  );

  const triggerReportsExport = useCallback(
    (params: ReportsExportParams) => {
      runGuardedExport({
        key: 'reports',
        runningRef: isReportsRunningRef,
        addKey,
        removeKey,
        runTask: () =>
          executeReportsExport(params, {
            fetchDataInBatches,
            xfetch,
            activeTenantName,
            canReadUsers,
          }),
      });
    },
    [addKey, removeKey, xfetch, fetchDataInBatches, activeTenantName, canReadUsers]
  );

  return (
    <ExportStateContext.Provider value={{ isExporting, triggerVolumesExport, triggerReportsExport }}>
      {children}
    </ExportStateContext.Provider>
  );
}

export function useExportState() {
  const context = useContext(ExportStateContext);
  if (!context) {
    throw new Error('useExportState must be used within ExportStateProvider');
  }
  return context;
}
